import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { selectProduct, setVizLoading } from '../../../../store/visualizerSlice';
import Product from '../../Product/Product';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './ProductGroupStep.module.css';

export default function ProductGroupStep({
    title,
    stepName,
    seriesId,
    surfaceName,
    applySurfaces = null,
    stepIndex,
    room = '',
    onViewAllApplied,
    onNext,
    onPrevious,
    hideNav = false,
    noFilter = false,
    onProductSelect = null,
    nameFilter = '',
    tierLabelFormatter = null,
    productPreFilter = null,
    groupingField: groupingFieldProp = null,
    forceBaseThumb = false
}) {
    const dispatch = useDispatch();
    const stepSelections = useSelector(state => state.visualizer.stepSelections);
    const activeFloorplanId = useSelector(state => state.visualizer.activeFloorplanId);
    const floorplans = useSelector(state => state.floorplan.floorplans);

    // Resolve the selected community's 2-letter Code from the active floorplan
    const communityCode = (() => {
        const fp = (floorplans || []).find(
            f => String(f.Id || f.id) === String(activeFloorplanId)
        );
        if (!fp) return '';
        const raw = fp.Code || fp.code || '';
        return String(raw).trim().toUpperCase();
    })();

    const [productData, setProductData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProductId, setSelectedProductId] = useState(null);

    const selectionKey = `${stepName}_${room}`;

    useEffect(() => {
        const savedSelection = stepSelections[selectionKey];
        if (savedSelection?.product?.id) {
            setSelectedProductId(savedSelection.product.id);
        }
    }, [stepSelections, selectionKey]);

    useEffect(() => {
        setLoading(true);
        setError(null);

        const URL = `https://lizard.chameleonpower.com/CDN/ProductsBySeries/${seriesId}.json`;

        fetch(URL)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch products: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                setProductData(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch((err) => {
                console.error(`${stepName} - Error fetching products:`, err);
                setError(err.message);
                setLoading(false);
            });
    }, [seriesId, stepName]);

    // Filter products by community code parsed from the `warranty` field.
    const communityFilteredProducts = (() => {
        if (noFilter || !communityCode) return productData;
        return productData.filter(p => {
            const warranty = p.warranty || p.Warranty || '';
            if (!warranty) return false;
            const codes = String(warranty)
                .split(/[\s,;/|]+/)
                .map(c => c.trim().toUpperCase())
                .filter(Boolean);
            return codes.includes(communityCode);
        });
    })();

    // Grouping field: explicit prop wins; otherwise pick by community code.
    const groupingField = (() => {
        if (groupingFieldProp) return groupingFieldProp;
        switch (communityCode) {
            case 'PR': return 'thumbnailRenderOptions';
            case 'AF': return 'style';
            case 'NG': return 'ecoFriendly';
            default:   return 'defaultRoomSceneSpecifier';
        }
    })();

    // Optional name-substring filter (case-insensitive, matches product.name).
    // `nameFilter` accepts a comma-separated list of substrings — a product passes
    // if any substring matches. This lets callers OR multiple keywords together.
    const nameFilteredProducts = (() => {
        const raw = String(nameFilter || '').trim().toLowerCase();
        let filtered = communityFilteredProducts;
        if (raw) {
            const tokens = raw.split(',').map(t => t.trim()).filter(Boolean);
            filtered = filtered.filter(p => {
                const name = String(p.name || '').toLowerCase();
                return tokens.some(t => name.includes(t));
            });
        }
        if (typeof productPreFilter === 'function') {
            filtered = filtered.filter(productPreFilter);
        }
        return filtered;
    })();

    const groupedProducts = (() => {
        const groups = {};
        nameFilteredProducts.forEach(p => {
            const label = p[groupingField] || '__all__';
            if (!groups[label]) groups[label] = [];
            groups[label].push(p);
        });
        return groups;
    })();

    const handleProductSelect = (product) => {
        setSelectedProductId(product.id);

        // Capture the same label the picker's section header shows so the
        // review/print pages can display it verbatim.
        const rawTier = product[groupingField] || '';
        let tierLabel = '';
        if (rawTier) {
            tierLabel = typeof tierLabelFormatter === 'function'
                ? String(tierLabelFormatter(rawTier, [product]) || '').trim()
                : String(rawTier).trim();
        }

        dispatch(selectProduct({
            product,
            surfaceName,
            stepIndex,
            selectionKey,
            tierLabel
        }));

        if (onProductSelect) {
            onProductSelect(product, { tier: rawTier, tierLabel });
            return;
        }

        const chamViz = document.getElementById('chamViz');
        if (!chamViz) {
            console.error(`${stepName} - chamViz element not found`);
            return;
        }

        if (!product.id) {
            console.error(`${stepName} - Product has no ID`);
            return;
        }

        dispatch(setVizLoading(true));

        const surfacesToMatch = Array.isArray(applySurfaces) && applySurfaces.length > 0
            ? applySurfaces
            : [surfaceName];

        chamViz.applyByFilter(
            product.id,
            "",
            function(newSel) {
                if (surfacesToMatch.includes(this.name)) {
                    return newSel;
                }
                return this.sel;
            },
            true
        );
    };

    const handleViewAllApplied = () => {
        if (onViewAllApplied) onViewAllApplied();
    };

    return (
        <div className={styles.container}>
            {!hideNav && (
                <StepNavigation
                    title={title}
                    onNext={onNext}
                    onPrevious={onPrevious}
                    onViewAllApplied={handleViewAllApplied}
                />
            )}

            <div className={styles.productListContainer}>
                {loading && (
                    <div className={styles.message}>Loading products...</div>
                )}

                {error && (
                    <div className={styles.errorMessage}>Error: {error}</div>
                )}

                {!loading && !error && Object.keys(groupedProducts).length === 0 && (
                    <div className={styles.message}>No products available.</div>
                )}

                {!loading && !error && Object.entries(groupedProducts).map(([tier, products]) => {
                    const displayTier = typeof tierLabelFormatter === 'function'
                        ? tierLabelFormatter(tier, products)
                        : tier;
                    return (
                    <div key={tier} className={styles.tierSection}>
                        {tier !== '__all__' && displayTier && (
                            <div className={styles.tierHeader}>
                                <span className={styles.tierTitle}>{displayTier}</span>
                                <div className={styles.tierLine}></div>
                            </div>
                        )}
                        <ul className={styles.productGrid}>
                            {products.map(product => (
                                <Product
                                    key={product.id}
                                    id={product.id}
                                    specifier={product.specifier}
                                    focus={500}
                                    size="256"
                                    title={product.color || ''}
                                    description={product.name || ''}
                                    series={String(seriesId)}
                                    onSelect={(id) => {
                                        const selected = products.find(p => p.id === id);
                                        if (selected) handleProductSelect(selected);
                                    }}
                                    isActive={product.id === selectedProductId}
                                    forceBaseThumb={forceBaseThumb}
                                />
                            ))}
                        </ul>
                    </div>
                    );
                })}
            </div>
        </div>
    );
}

ProductGroupStep.propTypes = {
    title: PropTypes.string.isRequired,
    stepName: PropTypes.string.isRequired,
    seriesId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    surfaceName: PropTypes.string.isRequired,
    applySurfaces: PropTypes.arrayOf(PropTypes.string),
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func,
    hideNav: PropTypes.bool,
    noFilter: PropTypes.bool,
    onProductSelect: PropTypes.func,
    nameFilter: PropTypes.string,
    tierLabelFormatter: PropTypes.func,
    productPreFilter: PropTypes.func,
    groupingField: PropTypes.string,
    forceBaseThumb: PropTypes.bool
};
